import type { EntriesQueries, Entry, EntrySkeletonType } from 'contentful';
import type { Locale } from '@tuic/i18n';
import { createConfiguredClientBySpace, type SPACE_NAME } from '../utils';
import { remember } from '@tuic/cache';
import { createContentTypeCacheTag, createSpaceCacheTag } from '../utils/cache';
import { onLocal } from '@tuic/lib/src/config';
import { logger } from '@tuic/logger';
import { ConditionalListQueries } from 'contentful';
import type {
  BrandNameProcessingConfig,
  ContentTypeConfig,
} from '../utils/brand-name-processor';
import {
  processEntries,
  processEntriesWithConfig,
} from '../utils/brand-name-processor';

export type EntryFromSkeleton<Skeleton extends EntrySkeletonType> = Entry<
  Skeleton,
  'WITHOUT_UNRESOLVABLE_LINKS',
  Locale
>;

type BaseQuery = {
  locale: Locale;
  content_type: string;
  select?: string[];
};

export type Query<Skeleton extends EntrySkeletonType> = BaseQuery &
  EntriesQueries<Skeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> &
  // Added to support querying for taxonomy concepts
  ConditionalListQueries<{ id: string }, any, 'metadata.concepts.sys', '[in]'>;

export type SingleEntryQuery<Skeleton extends EntrySkeletonType> =
  Query<Skeleton> & {
    limit?: undefined | 1;
  };

type GetEntriesArgs<Skeleton extends EntrySkeletonType> = {
  space: SPACE_NAME;
  query: Query<Skeleton>;
  preview?: boolean;
  brandNameProcessing?: BrandNameProcessingConfig | ContentTypeConfig<Skeleton>;
};

type GetEntryArgs<Skeleton extends EntrySkeletonType> = {
  space: SPACE_NAME;
  query: SingleEntryQuery<Skeleton>;
  preview?: boolean;
  brandNameProcessing?: BrandNameProcessingConfig | ContentTypeConfig<Skeleton>;
};

/**
 * Fetches entries from given space using given query.
 */
export const getEntries = async <Skeleton extends EntrySkeletonType>({
  space,
  query,
  preview,
  brandNameProcessing,
}: GetEntriesArgs<Skeleton>): Promise<EntryFromSkeleton<Skeleton>[] | []> => {
  try {
    let modifiedQuery = query;

    // Only modify the query if brandNameProcessing is enabled AND select exists
    if (brandNameProcessing && query.select) {
      modifiedQuery = { ...query }; // copy to avoid caching issues

      // Ensure select is an array and check if it already includes sys.contentType
      const selectArray = Array.isArray(modifiedQuery.select)
        ? [...modifiedQuery.select]
        : [];

      // Only add sys.contentType if not already included
      if (!selectArray.includes('sys.contentType')) {
        modifiedQuery.select = [...selectArray, 'sys.contentType'];
      }
    }

    const collection = await createConfiguredClientBySpace({
      space,
      preview,
    }).withoutUnresolvableLinks.getEntries<Skeleton>(modifiedQuery);

    // Process entries if brandNameProcessing is enabled
    if (!brandNameProcessing) {
      return collection.items;
    }

    // Use the typed version if it's a ContentTypeConfig
    if (typeof brandNameProcessing === 'object') {
      // TypeScript can't statically determine if this is a ContentTypeConfig<Skeleton>
      // but we maintain type safety through the processEntriesWithConfig function
      return processEntriesWithConfig<Skeleton, EntryFromSkeleton<Skeleton>>(
        collection.items,
        brandNameProcessing as ContentTypeConfig<Skeleton>,
      );
    }

    // Default case - use the standard processEntries function
    return processEntries(collection.items, brandNameProcessing);
  } catch (e) {
    logger.error(
      `[getEntries]: Contentful request failed. Query ${JSON.stringify(query)}`,
    );
    logger.error(e);
  }

  return [];
};

/**
 * Fetches the first entry from given space using given query.
 */
export const getEntry = async <Skeleton extends EntrySkeletonType>(
  arg: GetEntryArgs<Skeleton>,
): Promise<EntryFromSkeleton<Skeleton> | null> => {
  try {
    const entries = await getEntries<Skeleton>({
      ...arg,
      query: {
        ...arg.query,
        limit: 1,
      },
    });

    if (entries.length === 0) {
      return null;
    }

    return entries[0];
  } catch (e) {
    logger.error(
      `[getEntry]: Contentful request failed. Query ${JSON.stringify(arg.query)}`,
    );
    logger.error(e);
  }

  return null;
};

export const cachedGetEntry = async <Skeleton extends EntrySkeletonType>(
  arg: GetEntryArgs<Skeleton>,
): Promise<EntryFromSkeleton<Skeleton> | null> => {
  if (arg.preview === true) {
    return getEntry(arg);
  }

  let key = createQueryKey(arg.space, arg.query, arg.brandNameProcessing);

  if (!key) {
    logger.error(
      `Could not stringify query to key. This request will not be cached. You need to fix this before going to prod. Query: ${arg.query}`,
    );

    return getEntry(arg);
  }

  return remember(key, () => getEntry(arg), {
    tags: [
      createSpaceCacheTag(arg.space),
      createContentTypeCacheTag(arg.query.content_type),
    ],
  });
};

export const cachedGetEntries = async <Skeleton extends EntrySkeletonType>(
  arg: GetEntriesArgs<Skeleton>,
): Promise<EntryFromSkeleton<Skeleton>[] | []> => {
  if (arg.preview === true) {
    return getEntries(arg);
  }

  let key = createQueryKey(arg.space, arg.query, arg.brandNameProcessing);

  if (!key) {
    logger.error(
      `Could not stringify query to key. This request will not be cached. You need to fix this before going to prod. Query: ${arg.query}`,
    );

    return getEntries(arg);
  }

  return remember(key, () => getEntries(arg), {
    tags: [
      createSpaceCacheTag(arg.space),
      createContentTypeCacheTag(arg.query.content_type),
    ],
  });
};

const createQueryKey = (
  space: SPACE_NAME,
  query: BaseQuery,
  brandNameConfig?: BrandNameProcessingConfig,
): string | undefined => {
  try {
    let orderedQuery = (Object.keys(query) as unknown as (keyof typeof query)[])
      .sort()
      .reduce<Record<string, any>>((obj, key) => {
        obj[key] = query[key];
        return obj;
      }, {});

    let queryKey = JSON.stringify(orderedQuery);

    let configSuffix = '';

    if (brandNameConfig === true) {
      configSuffix = ':bnp';
    } else if (typeof brandNameConfig === 'object') {
      configSuffix = `:bnp:${JSON.stringify(brandNameConfig)}`;
    }

    return `q:${space}${configSuffix}:${queryKey}`;
  } catch (error) {
    const message = `Could not stringify query to cache key. This request will not be cached. You need to fix this before going to prod. Query: ${JSON.stringify(query) ?? query.toString()}`;

    if (onLocal()) {
      throw new Error(message);
    }

    logger.error(message);
  }

  return undefined;
};
